import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, RelationshipStageBadge, stageAccent, formatDate, useListState, sortRows, SortHeader } from "@/components/prospects/shared";
import { formatPhone } from "@/lib/phone";
import { Plus, MagnifyingGlass, CircleNotch, UsersThree, WarningCircle, FileXls, MapPin } from "@phosphor-icons/react";
import ImportProspectDialog from "@/components/prospects/ImportProspectDialog";

// Module-level request helpers: async logic and locals stay outside hook bodies.
async function fetchProspectsInto(setRows, params) {
  try {
    const { data } = await api.get("/prospects", { params });
    setRows(data);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  }
}

async function fetchFiltersInto(setSources, setUsers) {
  try {
    const [sources, users] = await Promise.all([
      api.get("/reference/lead-sources"),
      api.get("/users/options"),
    ]);
    setSources(sources.data);
    setUsers(users.data);
  } catch (e) {
    // Filters are non-critical — the list still renders without them.
    toast.error("Some filter options could not be loaded. Please refresh to try again.");
  }
}

const ANY = "__any__";

export default function Prospects() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [sources, setSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [showImport, setShowImport] = useState(false);
  // Filters, search and sort persist for the session so returning from a record
  // restores the exact list view.
  const [ls, setLs] = useListState("prospects", {
    q: "", stage: ANY, sourceId: ANY, salespersonId: ANY, status: ANY,
    marketCity: "", marketScope: "both", marketState: "",
    sortKey: "organization", sortDir: "asc",
  });
  const { q, stage, sourceId, salespersonId, status,
    marketCity = "", marketScope = "both", marketState = "" } = ls;
  const setPart = (k) => (v) => setLs((s) => ({ ...s, [k]: v }));
  const [setQ, setStage, setSourceId, setSalespersonId, setStatus] =
    [setPart("q"), setPart("stage"), setPart("sourceId"), setPart("salespersonId"), setPart("status")];
  const [setMarketCity, setMarketScope, setMarketState] =
    [setPart("marketCity"), setPart("marketScope"), setPart("marketState")];
  const marketActive = marketCity.trim().length > 0;
  const [expandedMarket, setExpandedMarket] = useState({});
  const onSort = (key) => setLs((s) => ({
    ...s, sortKey: key, sortDir: s.sortKey === key && s.sortDir === "asc" ? "desc" : "asc",
  }));
  const sorted = useMemo(() => (rows ? sortRows(rows, ls.sortKey, ls.sortDir) : null),
    [rows, ls.sortKey, ls.sortDir]);

  const params = useMemo(
    () => ({
      q: q.trim() || undefined,
      relationship_stage: stage === "__any__" ? undefined : stage,
      lead_source_id: sourceId === "__any__" ? undefined : sourceId,
      salesperson_id: salespersonId === "__any__" ? undefined : salespersonId,
      status: status === "__any__" ? undefined : status,
      market_city: marketCity.trim() || undefined,
      market_scope: marketCity.trim() ? marketScope : undefined,
      market_state: marketCity.trim() ? (marketState.trim() || undefined) : undefined,
    }),
    [q, stage, sourceId, salespersonId, status, marketCity, marketScope, marketState]
  );

  const helpers = useRef({ fetchProspectsInto, fetchFiltersInto });

  const load = useCallback(() => helpers.current.fetchProspectsInto(setRows, params), [helpers, setRows, params]);

  useEffect(() => {
    load();
  }, [load]);

  const loadFilters = useCallback(() => helpers.current.fetchFiltersInto(setSources, setUsers), [helpers, setSources, setUsers]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  return (
    <div className="p-6 sm:p-8" data-testid="prospects-page">
      <ImportProspectDialog open={showImport} onClose={() => setShowImport(false)} onDone={load} />
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#569A1F]">Sales</p>
          <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-[#032852] sm:text-4xl">Prospects</h1>
          <p className="mt-2 max-w-2xl text-sm text-[#6D6D70]">
            {isAdmin ? "All prospect relationships across the team." : "Prospect relationships assigned to you."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setShowImport(true)}
            className="rounded-sm font-semibold"
            data-testid="import-prospect-btn"
          >
            <FileXls size={18} className="text-[#1D6F42]" /> Import Prospect from Excel
          </Button>
          <Button
            onClick={() => navigate("/prospects/new")}
            className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]"
            data-testid="new-prospect-btn"
          >
            <Plus size={18} weight="bold" /> New Prospect
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D6D70]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search organization, contact name, email or phone..."
            className="rounded-sm pl-9"
            data-testid="prospect-search"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger className="rounded-sm sm:w-36" data-testid="filter-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All stages</SelectItem>
              <SelectItem value="cold">Cold — identified, no back-and-forth</SelectItem>
              <SelectItem value="warm">Warm — reciprocal communication</SelectItem>
              <SelectItem value="hot">Hot — proposal/negotiation</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sourceId} onValueChange={setSourceId}>
            <SelectTrigger className="rounded-sm sm:w-44" data-testid="filter-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All sources</SelectItem>
              {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Select value={salespersonId} onValueChange={setSalespersonId}>
              <SelectTrigger className="rounded-sm sm:w-44" data-testid="filter-salesperson"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All salespeople</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm sm:w-36" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* City/market search — saved HQ + Service Location address fields (no map pins) */}
      <div className="mb-4 rounded-sm border border-[#E4E4E7] bg-white px-4 py-3" data-testid="market-search-panel">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <div className="relative lg:w-72">
            <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D6D70]" />
            <Input
              value={marketCity}
              onChange={(e) => setMarketCity(e.target.value)}
              placeholder="City / market, e.g. Tampa"
              className="rounded-sm pl-9"
              data-testid="market-city-input"
            />
          </div>
          <Select value={marketScope} onValueChange={setMarketScope}>
            <SelectTrigger className="rounded-sm lg:w-64" data-testid="market-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="both">Search in: Both — HQ and Service Locations</SelectItem>
              <SelectItem value="hq">Search in: Headquarters</SelectItem>
              <SelectItem value="locations">Search in: Service Locations</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={marketState}
            onChange={(e) => setMarketState(e.target.value)}
            placeholder="State (optional)"
            className="rounded-sm lg:w-36"
            data-testid="market-state-input"
          />
          {marketActive && (
            <Button variant="ghost" size="sm" onClick={() => { setMarketCity(""); setMarketState(""); }}
              className="rounded-sm text-xs font-semibold text-[#6D6D70]" data-testid="market-clear-btn">
              Clear market search
            </Button>
          )}
        </div>
        <p className="mt-1.5 text-[11px] text-[#6D6D70]">
          Finds organizations headquartered in the city, with Service Locations there, or both — all industries and lifecycle stages. Partial, case-insensitive match on saved addresses.
        </p>
      </div>

      {/* Lifecycle stage legend — meaning is text-based, never color alone */}
      <p className="mb-4 text-xs leading-5 text-[#6D6D70]" data-testid="stage-legend">
        <span className="font-bold text-[#1D4ED8]">Cold</span> = identified, no back-and-forth&ensp;·&ensp;
        <span className="font-bold text-[#B45309]">Warm</span> = reciprocal communication&ensp;·&ensp;
        <span className="font-bold text-[#B91C1C]">Hot</span> = proposal/negotiation&ensp;·&ensp;
        <span className="font-bold text-[#569A1F]">Client</span> = formal client status&ensp;·&ensp;
        <span className="font-semibold">Needs review</span> = not yet classified
      </p>

      <div className="overflow-hidden rounded-sm border border-[#E4E4E7] bg-white">
        <div className="hidden grid-cols-12 gap-3 border-b border-[#E4E4E7] bg-[#F4F4F5] px-5 py-3 lg:grid">
          <SortHeader label="Organization" sortKey="organization" sort={ls} onSort={onSort} className="col-span-3" testId="sort-organization" />
          <SortHeader label="Primary contact" sortKey="contact" sort={ls} onSort={onSort} className="col-span-2" testId="sort-contact" />
          <div className="col-span-2 text-[10px] font-bold uppercase tracking-[0.15em] text-[#6D6D70]">Contact info</div>
          <SortHeader label="Stage" sortKey="stage" sort={ls} onSort={onSort} className="col-span-1" testId="sort-stage" />
          <SortHeader label="Source" sortKey="source" sort={ls} onSort={onSort} className="col-span-1" testId="sort-source" />
          <SortHeader label="Salesperson" sortKey="salesperson" sort={ls} onSort={onSort} className="col-span-1" testId="sort-salesperson" />
          <SortHeader label="Props" sortKey="properties" sort={ls} onSort={onSort} className="col-span-1 justify-center" testId="sort-properties" />
          <SortHeader label="Added" sortKey="added" sort={ls} onSort={onSort} className="col-span-1 justify-end" testId="sort-added" />
        </div>

        {sorted === null ? (
          <div className="flex items-center justify-center py-16">
            <CircleNotch size={24} className="animate-spin text-[#032852]" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <UsersThree size={32} className="text-[#032852]/30" />
            <p className="text-sm text-[#6D6D70]">No prospects found. Create your first one to get started.</p>
          </div>
        ) : (
          sorted.map((p) => (
            <div key={p.id} className="border-b border-[#E4E4E7] last:border-0">
            <button
              onClick={() => navigate(`/prospects/${p.id}`)}
              className="relative grid w-full grid-cols-2 items-center gap-3 py-4 pl-6 pr-5 text-left text-sm transition-colors hover:bg-[#FAFAFA] lg:grid-cols-12"
              data-testid={`prospect-row-${p.organization_name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
            >
              {/* Full-row lifecycle accent — solid core with a subtle fade, attached to the
                  row's outer left boundary and spanning the full row height. */}
              <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-2.5" data-testid={`stage-accent-${p.id}`}
                style={{ background: `linear-gradient(to right, ${stageAccent(p.relationship_stage, p.lifecycle_status)} 0px, ${stageAccent(p.relationship_stage, p.lifecycle_status)} 5px, transparent 100%)` }} />
              <div className="col-span-2 min-w-0 lg:col-span-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 truncate font-semibold text-[#09090B]">
                    {p.organization_name}
                    {p.duplicate_flag && isAdmin && (
                      <WarningCircle size={15} weight="fill" className="shrink-0 text-[#F59E0B]" title="Possible duplicate — flagged for review" data-testid={`duplicate-flag-${p.id}`} />
                    )}
                  </p>
                  <p className="truncate text-xs text-[#6D6D70] lg:hidden">
                    {p.primary_contact ? `${p.primary_contact.first_name} ${p.primary_contact.last_name}` : "—"} · {p.lead_source_name || "—"}
                  </p>
                </div>
              </div>
              <div className="hidden min-w-0 lg:col-span-2 lg:block">
                <p className="truncate text-[#52525B]">{p.primary_contact ? `${p.primary_contact.first_name} ${p.primary_contact.last_name}` : "—"}</p>
              </div>
              <div className="hidden min-w-0 lg:col-span-2 lg:block">
                <p className="truncate text-xs text-[#52525B]">{formatPhone(p.primary_contact?.office_phone || p.primary_contact?.mobile_phone) || "—"}</p>
                <p className="truncate text-xs text-[#6D6D70]">{p.primary_contact?.email || "—"}</p>
              </div>
              <div className="lg:col-span-1">
                <RelationshipStageBadge stage={p.relationship_stage} lifecycle={p.lifecycle_status}
                  reviewPending={p.stage_review_pending} testId={`stage-badge-${p.id}`}
                  onReviewClick={(e) => { e.stopPropagation(); navigate(`/prospects/${p.id}?review=stage`); }} />
              </div>
              <div className="hidden truncate text-xs text-[#52525B] lg:col-span-1 lg:block">{p.lead_source_name || "—"}</div>
              <div className="hidden truncate text-xs text-[#52525B] lg:col-span-1 lg:block">{p.assigned_salesperson_name || "—"}</div>
              <div className="hidden text-center lg:col-span-1 lg:block">
                <span className="text-xs font-semibold text-[#032852]">{p.property_count}</span>
                {p.open_opportunity_count > 0 && (
                  <span className="ml-1 rounded-sm bg-[#569A1F]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#457d18]" title={`${p.open_opportunity_count} active opportunit${p.open_opportunity_count === 1 ? "y" : "ies"}`} data-testid={`active-opps-${p.id}`}>
                    {p.open_opportunity_count} opp
                  </span>
                )}
              </div>
              <div className="hidden text-right lg:col-span-1 lg:block">
                <p className="text-xs text-[#52525B]">{formatDate(p.date_entered)}</p>
                <div className="mt-0.5 flex justify-end"><StatusBadge status={p.status} /></div>
              </div>
            </button>
            {marketActive && p.market_match && (
              <div className="bg-[#F8FAF5] px-6 pb-3 pt-1" data-testid={`market-match-${p.id}`}>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`rounded-sm px-2 py-0.5 font-bold uppercase tracking-wide ${
                    p.market_match === "both" ? "bg-[#032852] text-white"
                      : p.market_match === "headquarters" ? "bg-[#1D4ED8]/10 text-[#1D4ED8]"
                      : "bg-[#569A1F]/15 text-[#457d18]"}`}
                    data-testid={`market-match-badge-${p.id}`}>
                    {p.market_match === "both" ? "HQ + Service Locations"
                      : p.market_match === "headquarters" ? "Headquarters" : "Service Locations"}
                  </span>
                  {p.market_match !== "service_locations" && p.hq_city && (
                    <span className="text-[#52525B]">HQ: {[p.hq_street_address, p.hq_city, p.hq_state, p.hq_zip].filter(Boolean).join(", ")}</span>
                  )}
                  {p.matched_location_count > 0 && (
                    <button
                      onClick={() => setExpandedMarket((s) => ({ ...s, [p.id]: !s[p.id] }))}
                      className="font-semibold text-[#032852] underline-offset-2 hover:underline"
                      data-testid={`market-expand-${p.id}`}
                    >
                      {p.matched_location_count} matching Service Location{p.matched_location_count === 1 ? "" : "s"} {expandedMarket[p.id] ? "▴" : "▾"}
                    </button>
                  )}
                </div>
                {expandedMarket[p.id] && p.matched_locations?.length > 0 && (
                  <ul className="mt-2 space-y-1" data-testid={`market-locations-${p.id}`}>
                    {p.matched_locations.map((loc) => (
                      <li key={loc.id}>
                        <button
                          onClick={() => navigate(`/prospects/${p.id}?tab=properties`)}
                          className="flex w-full items-center gap-2 rounded-sm border border-[#E4E4E7] bg-white px-3 py-1.5 text-left text-xs hover:border-[#569A1F]/50 hover:bg-[#FAFAFA]"
                          data-testid={`market-location-${loc.id}`}
                        >
                          <MapPin size={13} className="shrink-0 text-[#569A1F]" />
                          <span className="font-semibold text-[#09090B]">{loc.name}</span>
                          <span className="truncate text-[#6D6D70]">
                            {[loc.street_address, loc.city, loc.state, loc.zip_code].filter(Boolean).join(", ")}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
